import EngineModule from '@/store/engine/modules/EngineModule';
import { OwnerType, SessionOwnerInput, SessionStatusEnum } from '@/store/engine/openapi/generated';
import { Store } from '@/store/mainStore';
import { action } from 'mobx';
import {
	ProfileService,
	UserRecordInput,
	CreatePDFInput,
	OrderService,
	UserInput,
	FitStationMedicalSolutionDataInput,
	FitStationSolutionDataInput
} from '@/store/engine/openapi/generated';

export type optionalUpdateData = {
	natsData?: SessionStatusEnum;
	update: boolean;
};

export default class ProfileModule extends EngineModule {
	constructor(s: Store) {
		super(s);
	}

	public async createUserRecord(userRecordInput: UserRecordInput) {
		return await ProfileService.profileControllerCreateUserRecord(userRecordInput).then((c) => c);
	}

	@action
	public async createSession(userID: string = this.store._patient.userRecordID) {
		const employeeID = this.store.engine.auth.token?.parsedToken.user_id;

		const store = this.store.engine.store.getStore;

		if (!userID) {
			throw new Error('Patient userID not found');
		}

		if (!employeeID) {
			throw new Error('Employee ID not found.');
		}

		if (!store.companyID) {
			throw new Error('companyID not found.');
		}

		if (!store.storeID) {
			throw new Error('siteID not found');
		}

		const owners: SessionOwnerInput[] = [
			{
				ownerID: employeeID,
				ownerType: OwnerType.OWNER_TYPE_OPERATOR
			} as SessionOwnerInput,
			{
				ownerID: userID,
				ownerType: OwnerType.OWNER_TYPE_USER
			} as SessionOwnerInput,
			{
				ownerID: store.companyID,
				ownerType: OwnerType.OWNER_TYPE_COMPANY
			} as SessionOwnerInput,
			{
				ownerID: store.storeID,
				ownerType: OwnerType.OWNER_TYPE_SITE
			} as SessionOwnerInput
		];

		return ProfileService.profileControllerCreateSession({ owners }).then((s) => s.sessionID);
	}

	public async createPDF(createPDFInput: CreatePDFInput) {
		return await ProfileService.profileControllerCreatePdf(createPDFInput).then((p) => p);
	}

	public async getUser(userID: string) {
		if (!userID) {
			throw new Error('User ID not found');
		}

		return await ProfileService.profileControllerUser(userID).then((u) => u);
	}

	@action
	public async getFitstationProfile(userID: string = this.store._patient.userRecordID) {
		return await ProfileService.profileControllerFitStationProfile(userID).then((f) => f);
	}

	public async copyFitstationProfile(userID: string, oldUserID: string, sessionID: string, oldSessionId: string) {
		return await ProfileService.profileControllerCopyFitStationProfile(userID, oldUserID, sessionID, oldSessionId).then((f) => f);
	}

	public async getFitstationMedicalProfile(userID: string = this.store._patient.userRecordID) {
		return await ProfileService.profileControllerFitStationMedicalProfile(userID).then((f) => f);
	}

	public async getFitstationMedicalProfileForOrder(userID: string = this.store._patient.userRecordID) {
		return await ProfileService.profileControllerFitStationMedicalProfile(userID).then((f) => f.data);
	}

	public async updateUserOwner(userID: string = this.store._patient.userRecordID, ownerID: string = this.store._patient.patientUIData.assignedToDoctor) {
		if (!userID) {
			throw new Error('UserID not found');
		}

		if (!ownerID) {
			throw new Error('OwnerID not found');
		}

		return ProfileService.profileControllerUpdateCurrentOwner({ userID, ownerID }).then((u) => u);
	}

	public async updateUser(userID: string, user: UserInput) {
		return await ProfileService.profileControllerUpdateUser(userID, user).then((u) => u.userID);
	}

	public async updateFitstationMedicalProfile(userID: string, profileData: FitStationMedicalSolutionDataInput, optional: optionalUpdateData) {
		if (optional?.natsData) optional.update = true;

		return ProfileService.profileControllerUpdateFitStationMedicalProfile(
			userID,
			profileData,
			optional.natsData,
			this.store._patient.currentSessionID,
			optional.update
		).then((u) => u.userID);
	}

	public async updateFitStationProfile(userID: string, profileData: FitStationSolutionDataInput) {
		if (!userID) {
			throw new Error('UserID not found');
		}

		if (!profileData.lastValidScanningSession) {
			throw new Error('SessionID not found');
		}

		return ProfileService.profileControllerUpdateFitStationProfile(userID, profileData).then((u) => u.userID);
	}

	public async deleteUser(userID: string) {
		if (!userID) {
			throw new Error('UserID not found');
		}

		return await ProfileService.profileControllerDeleteUser(userID).then((u) => u);
	}
}
